import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, SectionTitle } from '@/components/Card';
import { Badge, type BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingScreen';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useBusinessUnits } from '@/app/hooks';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import {
  invitationsService,
  usersService,
  type Invitation,
  type InvitationIssued,
  type User,
} from '@/services/users.service';
import { telegramService, type TelegramLink } from '@/services/telegram.service';
import { ApiRequestError } from '@/services/api';
import { roleTitle } from '@/types/permission';
import { InviteUserDialog, inviteErrorMessage } from './InviteUserDialog';
import { ModuleAccessDialog } from './ModuleAccessDialog';
import { v } from '@/theme/tokens';

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Активен',
  INVITED: 'Приглашён',
  BLOCKED: 'Отключён',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  INVITED: 'warning',
  BLOCKED: 'negative',
};

function scopeLabel(scopeAll: boolean, units: { name: string }[]): string {
  if (scopeAll) return 'Вся компания';
  if (units.length === 0) return 'Объекты не назначены';
  return units.map((u) => u.name).join(', ');
}

/** What the owner reads next to a person: state, never a token. */
function telegramLabel(user: User): string {
  const state = user.telegram;
  if (state?.connected) {
    return state.username ? `@${state.username} / Подключён` : 'Подключён';
  }
  if (state?.link_pending) return 'Ссылка выдана, ожидает подключения';
  return 'Не подключён';
}

function Avatar({ text }: { text: string }) {
  return (
    <div
      aria-hidden
      style={{
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: 999,
        background: v('accent-soft'),
        color: v('accent-ink'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      {text.charAt(0).toUpperCase()}
    </div>
  );
}

/** Owner-only. Every change here is re-checked and audited on the server. */
export function UsersSection() {
  const client = useQueryClient();
  const wide = useIsWideScreen();
  const [pendingRole, setPendingRole] = useState<{ user: User; role: string } | null>(null);
  const [pendingDisable, setPendingDisable] = useState<User | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<Invitation | null>(null);
  const [scopeEditing, setScopeEditing] = useState<User | null>(null);
  // «Дополнительный доступ» — operational modules, owner-only on the server.
  const [moduleEditing, setModuleEditing] = useState<User | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [issued, setIssued] = useState<InvitationIssued | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // The Telegram link is shown once, next to the user it was minted for.
  const [telegramLink, setTelegramLink] = useState<{ userId: string; link: TelegramLink } | null>(
    null,
  );
  const [telegramCopied, setTelegramCopied] = useState(false);
  const [pendingTelegramOff, setPendingTelegramOff] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const units = useBusinessUnits();

  const users = useQuery({
    queryKey: ['users'],
    queryFn: ({ signal }) => usersService.list(signal),
  });

  const roles = useQuery({
    queryKey: ['users', 'assignable-roles'],
    queryFn: ({ signal }) => usersService.assignableRoles(signal),
  });

  const invitations = useQuery({
    queryKey: ['invitations'],
    queryFn: ({ signal }) => invitationsService.list(signal),
  });

  // Only `configured` is used here: whether the installation has a bot at all.
  const telegram = useQuery({
    queryKey: ['telegram', 'status'],
    queryFn: ({ signal }) => telegramService.status(signal),
  });

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['users'] });
    void client.invalidateQueries({ queryKey: ['invitations'] });
    void client.invalidateQueries({ queryKey: ['audit'] });
  };

  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiRequestError ? e.message : fallback);

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      usersService.changeRole(userId, role),
    onSuccess: () => {
      setPendingRole(null);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось изменить роль'),
  });

  const changeScope = useMutation({
    mutationFn: ({ userId, scopeAll, unitIds }: { userId: string; scopeAll: boolean; unitIds: string[] }) =>
      usersService.changeScope(userId, {
        scope_all_units: scopeAll,
        business_unit_ids: unitIds,
      }),
    onSuccess: () => {
      setScopeEditing(null);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось изменить доступ'),
  });

  const setEnabled = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      enabled ? usersService.enable(userId) : usersService.disable(userId),
    onSuccess: () => {
      setPendingDisable(null);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось изменить статус'),
  });

  const createTelegramLink = useMutation({
    mutationFn: (userId: string) => usersService.telegramLink(userId),
    onSuccess: (link, userId) => {
      setTelegramLink({ userId, link });
      setTelegramCopied(false);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось создать ссылку подключения Telegram'),
  });

  const revokeTelegramLink = useMutation({
    mutationFn: (userId: string) => usersService.telegramRevokeLink(userId),
    onSuccess: () => {
      setTelegramLink(null);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось отозвать ссылку'),
  });

  const disconnectTelegram = useMutation({
    mutationFn: (userId: string) => usersService.telegramDisconnect(userId),
    onSuccess: () => {
      setPendingTelegramOff(null);
      setTelegramLink(null);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось отключить Telegram'),
  });

  const copyTelegramLink = async () => {
    if (!telegramLink) return;
    try {
      await navigator.clipboard?.writeText(telegramLink.link.link);
      setTelegramCopied(true);
    } catch {
      setTelegramCopied(false);
    }
  };

  const createInvite = useMutation({
    mutationFn: invitationsService.create,
    onSuccess: (data) => {
      setIssued(data);
      setError(null);
      refresh();
    },
    onError: (e: unknown) => setError(inviteErrorMessage(e)),
  });

  const revokeInvite = useMutation({
    mutationFn: (id: string) => invitationsService.revoke(id),
    onSuccess: () => {
      setPendingRevoke(null);
      setError(null);
      refresh();
    },
    onError: fail('Не удалось отменить приглашение'),
  });

  const renewInvite = useMutation({
    mutationFn: (id: string) => invitationsService.renew(id),
    onSuccess: async (data) => {
      setError(null);
      refresh();
      try {
        await navigator.clipboard?.writeText(data.invite_link);
        setCopiedId(data.invitation.id);
      } catch {
        setCopiedId(null);
      }
    },
    onError: fail('Не удалось перевыпустить приглашение'),
  });

  const pending = (invitations.data ?? []).filter((i) => i.state === 'PENDING' || i.state === 'EXPIRED');

  return (
    <Card>
      <SectionTitle
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge tone="neutral">{users.data?.length ?? 0}</Badge>
            <Button
              variant="primary"
              style={{ padding: '6px 12px', fontSize: 12.5 }}
              onClick={() => {
                setIssued(null);
                setInviteOpen(true);
              }}
            >
              + Пригласить
            </Button>
          </div>
        }
      >
        Пользователи и роли
      </SectionTitle>

      {(users.isPending || invitations.isPending) && <Spinner />}
      {users.error && (
        <ErrorState
          message={users.error instanceof Error ? users.error.message : 'Ошибка загрузки'}
          onRetry={() => void users.refetch()}
        />
      )}
      {error && (
        <div
          role="alert"
          style={{
            background: v('negative-soft'),
            color: v('negative'),
            padding: '9px 12px',
            borderRadius: 10,
            fontSize: 13,
            marginBottom: 10,
          }}
        >
          {error}
        </div>
      )}

      {users.data?.map((item) => (
        <div
          key={item.id}
          style={{
            display: 'flex',
            alignItems: wide ? 'center' : 'flex-start',
            gap: 12,
            padding: '11px 0',
            borderBottom: `1px solid ${v('line-soft')}`,
            flexWrap: 'wrap',
            flexDirection: wide ? 'row' : 'column',
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1, minWidth: 200 }}>
            <Avatar text={item.full_name || item.email} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.full_name}</div>
              <div style={{ fontSize: 12, color: v('ink-muted') }}>{item.email}</div>
              <div style={{ fontSize: 11.5, color: v('ink-faint'), marginTop: 2 }}>
                {scopeLabel(item.scope_all_units, item.scope_business_units)}
              </div>
              <div style={{ fontSize: 11.5, color: v('ink-muted'), marginTop: 2 }}>
                Telegram: {telegramLabel(item)}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge tone="neutral">{roleTitle(item.role.code)}</Badge>
            <Badge tone={STATUS_TONE[item.status] ?? 'neutral'}>
              {STATUS_LABEL[item.status] ?? item.status}
            </Badge>

            {item.telegram?.connected ? (
              <Button
                variant="danger"
                style={{ padding: '6px 10px', fontSize: 12 }}
                onClick={() => setPendingTelegramOff(item)}
              >
                Отключить Telegram
              </Button>
            ) : (
              <>
                <Button
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  disabled={!telegram.data?.configured || createTelegramLink.isPending}
                  onClick={() => createTelegramLink.mutate(item.id)}
                >
                  Подключить Telegram
                </Button>
                {item.telegram?.link_pending && (
                  <Button
                    style={{ padding: '6px 10px', fontSize: 12 }}
                    disabled={revokeTelegramLink.isPending}
                    onClick={() => revokeTelegramLink.mutate(item.id)}
                  >
                    Отозвать ссылку
                  </Button>
                )}
              </>
            )}

            <Button
              style={{ padding: '6px 10px', fontSize: 12 }}
              onClick={() => setModuleEditing(item)}
            >
              Дополнительный доступ
            </Button>

            {item.is_protected ? (
              <span style={{ fontSize: 12, color: v('ink-muted') }}>роль защищена</span>
            ) : (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ position: 'absolute', left: -9999 }}>
                    Роль пользователя {item.full_name}
                  </span>
                  <select
                    value=""
                    onChange={(event) => {
                      if (event.target.value) setPendingRole({ user: item, role: event.target.value });
                    }}
                    style={{
                      padding: '6px 10px',
                      border: `1px solid ${v('line')}`,
                      borderRadius: 9,
                      background: v('surface'),
                      color: v('ink'),
                      fontSize: 12.5,
                    }}
                  >
                    <option value="">Изменить роль…</option>
                    {roles.data
                      ?.filter((r) => r.code !== item.role.code)
                      .map((r) => (
                        <option key={r.code} value={r.code}>
                          {r.name}
                        </option>
                      ))}
                  </select>
                </label>

                <Button
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => setScopeEditing(item)}
                >
                  Доступ
                </Button>

                {item.status === 'BLOCKED' ? (
                  <Button
                    style={{ padding: '6px 10px', fontSize: 12 }}
                    onClick={() => setEnabled.mutate({ userId: item.id, enabled: true })}
                  >
                    Включить
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    style={{ padding: '6px 10px', fontSize: 12 }}
                    onClick={() => setPendingDisable(item)}
                  >
                    Отключить
                  </Button>
                )}
              </>
            )}
          </div>

          {telegramLink?.userId === item.id && (
            <div style={{ width: '100%', minWidth: 0 }}>
              <div style={{ fontSize: 11.5, color: v('ink-muted'), marginBottom: 4 }}>
                Ссылка подключения для {item.full_name}
              </div>
              <div
                style={{
                  background: v('panel'),
                  border: `1px solid ${v('line')}`,
                  borderRadius: 9,
                  padding: '8px 10px',
                  fontSize: 12,
                  // Long token, narrow phone: wrap instead of widening the page.
                  wordBreak: 'break-all',
                  overflowWrap: 'anywhere',
                  marginBottom: 8,
                }}
              >
                {telegramLink.link.link}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant="primary"
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => void copyTelegramLink()}
                >
                  {telegramCopied ? 'Скопировано' : 'Скопировать ссылку'}
                </Button>
                <Button
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => setTelegramLink(null)}
                >
                  Скрыть
                </Button>
              </div>
              <p style={{ fontSize: 11.5, color: v('ink-faint'), marginTop: 8, marginBottom: 0 }}>
                Передайте ссылку пользователю. Она одноразовая, действует 15 минут и показывается
                только сейчас.
              </p>
            </div>
          )}
        </div>
      ))}

      {pending.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              color: v('ink-muted'),
              marginBottom: 8,
            }}
          >
            Приглашения
          </div>

          {pending.map((invitation) => (
            <div
              key={invitation.id}
              style={{
                display: 'flex',
                alignItems: wide ? 'center' : 'flex-start',
                gap: 12,
                padding: '11px 0',
                borderBottom: `1px solid ${v('line-soft')}`,
                flexWrap: 'wrap',
                flexDirection: wide ? 'row' : 'column',
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1, minWidth: 200 }}>
                <Avatar text={invitation.full_name || invitation.email} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{invitation.full_name}</div>
                  <div style={{ fontSize: 12, color: v('ink-muted') }}>{invitation.email}</div>
                  <div style={{ fontSize: 11.5, color: v('ink-faint'), marginTop: 2 }}>
                    {scopeLabel(invitation.scope_all_units, invitation.scope_business_units)}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Badge tone="neutral">{roleTitle(invitation.role.code)}</Badge>
                <Badge tone={invitation.state === 'EXPIRED' ? 'negative' : 'warning'}>
                  {invitation.state === 'EXPIRED' ? 'Срок истёк' : 'Ожидает принятия'}
                </Badge>

                <Button
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => renewInvite.mutate(invitation.id)}
                  disabled={renewInvite.isPending}
                >
                  {copiedId === invitation.id ? 'Ссылка скопирована' : 'Новая ссылка'}
                </Button>
                <Button
                  variant="danger"
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => setPendingRevoke(invitation)}
                >
                  Отменить
                </Button>
              </div>
            </div>
          ))}

          <p style={{ fontSize: 11.5, color: v('ink-faint'), marginTop: 8, marginBottom: 0 }}>
            Ссылка показывается один раз при создании. Кнопка «Новая ссылка» выпускает свежую и
            копирует её — предыдущая перестаёт работать.
          </p>
        </div>
      )}

      <p style={{ fontSize: 12, color: v('ink-muted'), marginTop: 12, marginBottom: 0 }}>
        Telegram подключает только владелец: ссылка выпускается для конкретного пользователя,
        действует один раз и хранится на сервере лишь в виде хэша. Роль владельца нельзя назначить
        через это меню — передача прав выполняется отдельной
        процедурой. Пользователи не удаляются: отключённая учётная запись сохраняет свою историю.
        Каждое изменение записывается в журнал аудита. «Дополнительный доступ» открывает
        операционные модули — инвентаризацию, поставщиков и документы; выдавать его может только
        владелец, и он не расширяет область доступа к объектам.
      </p>

      {inviteOpen && (
        <InviteUserDialog
          roles={roles.data ?? []}
          units={units.data ?? []}
          busy={createInvite.isPending}
          issued={issued}
          onSubmit={(input) => createInvite.mutate(input)}
          onClose={() => {
            setInviteOpen(false);
            setIssued(null);
          }}
        />
      )}

      {moduleEditing && (
        <ModuleAccessDialog
          userId={moduleEditing.id}
          userName={moduleEditing.full_name || moduleEditing.email}
          onClose={() => setModuleEditing(null)}
        />
      )}

      {scopeEditing && (
        <ScopeDialog
          user={scopeEditing}
          units={units.data ?? []}
          busy={changeScope.isPending}
          onSubmit={(scopeAll, unitIds) =>
            changeScope.mutate({ userId: scopeEditing.id, scopeAll, unitIds })
          }
          onClose={() => setScopeEditing(null)}
        />
      )}

      {pendingRole && (
        <ConfirmDialog
          title="Изменить роль"
          message={`Изменить роль пользователя ${pendingRole.user.full_name} с «${roleTitle(
            pendingRole.user.role.code,
          )}» на «${roleTitle(pendingRole.role)}»?`}
          confirmLabel="Изменить роль"
          busy={changeRole.isPending}
          onCancel={() => setPendingRole(null)}
          onConfirm={() => changeRole.mutate({ userId: pendingRole.user.id, role: pendingRole.role })}
        />
      )}

      {pendingDisable && (
        <ConfirmDialog
          title="Отключить пользователя"
          message={`Отключить ${pendingDisable.full_name}? Учётная запись сохранится вместе со всей историей действий, но вход станет невозможен.`}
          confirmLabel="Отключить"
          tone="danger"
          busy={setEnabled.isPending}
          onCancel={() => setPendingDisable(null)}
          onConfirm={() => setEnabled.mutate({ userId: pendingDisable.id, enabled: false })}
        />
      )}

      {pendingTelegramOff && (
        <ConfirmDialog
          title="Отключить Telegram"
          message={`Отключить Telegram у ${pendingTelegramOff.full_name}? Бот перестанет отвечать этому аккаунту, данные останутся на месте.`}
          confirmLabel="Отключить Telegram"
          tone="danger"
          busy={disconnectTelegram.isPending}
          onCancel={() => setPendingTelegramOff(null)}
          onConfirm={() => disconnectTelegram.mutate(pendingTelegramOff.id)}
        />
      )}

      {pendingRevoke && (
        <ConfirmDialog
          title="Отменить приглашение"
          message={`Отменить приглашение для ${pendingRevoke.email}? Ссылка перестанет работать.`}
          confirmLabel="Отменить приглашение"
          tone="danger"
          busy={revokeInvite.isPending}
          onCancel={() => setPendingRevoke(null)}
          onConfirm={() => revokeInvite.mutate(pendingRevoke.id)}
        />
      )}
    </Card>
  );
}

function ScopeDialog({
  user,
  units,
  busy,
  onSubmit,
  onClose,
}: {
  user: User;
  units: { id: string; name: string; type: string }[];
  busy: boolean;
  onSubmit: (scopeAll: boolean, unitIds: string[]) => void;
  onClose: () => void;
}) {
  const [scopeAll, setScopeAll] = useState(user.scope_all_units);
  const [selected, setSelected] = useState<string[]>(
    user.scope_business_units.map((u) => u.id),
  );
  const [error, setError] = useState<string | null>(null);

  return (
    <div
      className="rt-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 60,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Область доступа"
    >
      <div
        className="rt-dialog"
        style={{
          borderRadius: 16,
          padding: 20,
          width: 440,
          maxWidth: '100%',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Область доступа</div>
        <div style={{ fontSize: 12.5, color: v('ink-muted'), marginBottom: 14 }}>
          {user.full_name}
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, fontSize: 13.5 }}>
          <input
            type="radio"
            name="scope-edit"
            checked={scopeAll}
            onChange={() => setScopeAll(true)}
            aria-label="Вся компания"
          />
          Вся компания
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 13.5 }}>
          <input
            type="radio"
            name="scope-edit"
            checked={!scopeAll}
            onChange={() => setScopeAll(false)}
            aria-label="Выбранные объекты"
          />
          Выбранные объекты
        </label>

        {!scopeAll && (
          <div style={{ paddingLeft: 22 }}>
            {units.map((unit) => (
              <label
                key={unit.id}
                style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0', fontSize: 13 }}
              >
                <input
                  type="checkbox"
                  aria-label={unit.name}
                  checked={selected.includes(unit.id)}
                  onChange={() =>
                    setSelected((current) =>
                      current.includes(unit.id)
                        ? current.filter((u) => u !== unit.id)
                        : [...current, unit.id],
                    )
                  }
                />
                {unit.name}
              </label>
            ))}
          </div>
        )}

        {error && (
          <div role="alert" style={{ color: v('negative'), fontSize: 12.5, marginTop: 10 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => {
              if (!scopeAll && selected.length === 0) {
                setError('Выберите хотя бы один объект или дайте доступ ко всей компании');
                return;
              }
              onSubmit(scopeAll, scopeAll ? [] : selected);
            }}
          >
            {busy ? 'Сохранение…' : 'Сохранить'}
          </Button>
          <Button onClick={onClose}>Отмена</Button>
        </div>
      </div>
    </div>
  );
}
